import authProvider from '../authProvider';
import tokenExpirationGraceService from './tokenExpirationGraceService';
import statePreservationService from './statePreservationService';

/**
 * Service to handle automatic token refresh
 * This ensures tokens are refreshed before they expire
 *
 * Updated to work with grace period - will attempt refresh but won't
 * force logout during grace period
 */
class TokenRefreshService {
  private refreshInterval: NodeJS.Timeout | null = null;
  private readonly REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private readonly TOKEN_EXPIRY_BUFFER = 2 * 60 * 1000; // 2 minutes before expiry

  /**
   * Start automatic token refresh
   */
  start() {
    if (this.refreshInterval) {
      return; // Already started
    }

    console.log('Starting automatic token refresh service...');

    this.refreshInterval = setInterval(async () => {
      await this.checkAndRefreshToken();
    }, this.REFRESH_INTERVAL);

    // Don't check immediately on start - wait for authentication
    // this.checkAndRefreshToken();
  }

  /**
   * Stop automatic token refresh
   */
  stop() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Stopped automatic token refresh service');
    }
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private async checkAndRefreshToken() {
    try {
      const auth = authProvider.auth;

      // Only check if user is authenticated
      if (!auth.isAuthenticated()) {
        return;
      }

      // Check if we have a valid access token first
      const accessToken = await auth.getAccessToken();
      if (!accessToken) {
        console.log('No access token available, skipping refresh check');
        return;
      }

      // Check if token is expired or will expire soon
      const isTokenExpired = await auth.isTokenExpire();
      if (isTokenExpired) {
        // Check if we're in grace period
        if (tokenExpirationGraceService.isInGracePeriod()) {
          console.log(
            'Token expired but in grace period, attempting background refresh...'
          );
        } else {
          console.log('Token expired or expiring soon, refreshing...');
        }

        try {
          await auth.fetchRefreshToken();
          console.log('Token refresh successful');
          // End grace period and restore state after successful refresh
          tokenExpirationGraceService.endGracePeriod();
          statePreservationService.restoreState();
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If in grace period, don't force logout - let user continue
          if (!tokenExpirationGraceService.isInGracePeriod()) {
            // The auth client will handle logout/redirect on failure
            // But we'll start grace period first
            const expirationTime = Date.now();
            tokenExpirationGraceService.startGracePeriod(expirationTime);
          }
        }
      }
    } catch (error) {
      console.error('Error in token refresh check:', error);
    }
  }

  /**
   * Manually trigger token refresh
   */
  async refreshToken() {
    try {
      const auth = authProvider.auth;
      await auth.fetchRefreshToken();
      console.log('Manual token refresh successful');
      return true;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
